/**
 * Partner Branding API client — talks to /api/retailer-by-subdomain.php.
 *
 * Public endpoint, no credentials. Returns null on 404 so the caller can
 * fall back to the default Trusscore experience without try/catch noise.
 */

import type {
  CurrencyCode,
  RetailerNavLink,
  RetailerSplashConfig,
  RetailerTheme,
  TotalFormat,
} from './adminRetailersApi';

const API_BASE = '/api';

/**
 * Trimmed branding payload returned by the public resolver. Excludes
 * pricing, addresses, contact info, and any other admin-only fields —
 * what a customer-facing browser actually needs to render the partner
 * experience and nothing else.
 */
export interface PartnerBranding {
  id: number;
  name: string;
  subdomain: string;
  isPartner: true;
  eligibilityOk: boolean;
  missingAcronyms: string[];
  theme: RetailerTheme | null;
  logoUrl: string | null;
  logoAlt: string | null;
  /** The partner's own picture watermark, in place of the site's default (null: the default). */
  watermarkUrl?: string | null;
  splashConfig: RetailerSplashConfig | null;
  navLinks: RetailerNavLink[] | null;
  underConstructionMessage: string | null;
  /** Force price/total columns on for this retailer's shared estimates + PDFs. */
  showTotals: boolean;
  /** Estimated-total display format for this retailer. */
  totalFormat: TotalFormat;
  /** Currencies this retailer offers. One entry → forced currency, no toggle. */
  currenciesSupported: CurrencyCode[];
  /** Language this portal opens in before any visitor override. Null → global default. */
  defaultLocale: string | null;
  /** Locales this portal offers. Null/empty → every active locale. */
  localesSupported: string[] | null;
}

/**
 * Fetch branding by subdomain slug. Returns null on 404 (unknown,
 * inactive, or non-partner) so callers can fall back to defaults. Other
 * network errors throw — the caller decides how loudly to surface them.
 */
export async function fetchPartnerBySubdomain(slug: string): Promise<PartnerBranding | null> {
  const res = await fetch(
    `${API_BASE}/retailer-by-subdomain.php?slug=${encodeURIComponent(slug)}`,
    { credentials: 'omit' },
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Partner branding fetch failed (${res.status})`);
  return res.json() as Promise<PartnerBranding>;
}
